import { useEffect, useRef, useState } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { Pencil, Eye, CircleDollarSign, Link2, PackageCheck, Wallet, ListChecks, History } from "lucide-react"
import { Page, Card, Button, Alert, Loading, ProcessStepper, ProgressBar } from "./ui"
import StatusBadge from "./components/StatusBadge"
import LifecycleTimeline from "./components/LifecycleTimeline"
import PurchaseActionBar, { PO_ACTION_META } from "./components/purchase/PurchaseActionBar"
import GoodsReceiptPanel from "./components/purchase/GoodsReceiptPanel"
import GoodsReceiptHistoryDrawer from "./components/purchase/GoodsReceiptHistoryDrawer"
import { receiptStatusOf } from "./receiptStatus"
import { StatusPill } from "procurement/design"
import MissingPricesDialog from "./components/purchase/MissingPricesDialog"
import { VendorProposalsSection } from "./components/approvals/ManagerOrderSections"
import {
  useGetPurchaseQuery,
  useGetPurchasePriceReadinessQuery,
  useSubmitPurchaseMutation,
  usePurchaseActionMutation,
  useResubmitPurchaseMutation,
  useIssuePurchaseMutation,
  useReceivePurchaseMutation,
  useSendPurchaseToVendorMutation,
  useResendPurchaseToVendorMutation,
  useCancelPurchaseMutation,
} from "shared/redux/endpoints/procurementPurchases"
import { showToastSuccess, showToastError, showToastWarning } from "utilities/toastUtil"
import { canEditPurchase, PO_STATUS } from "./status"
import { isAutoRejectedOutcome, AUTO_REJECT_MESSAGE } from "./actionResultFeedback"
import { getApiErrorMessage } from "./orderSubmitFeedback"
import {
  PAYMENT_METHOD_OPTIONS,
  formatVND,
  formatDate,
  formatDateTime,
  formatMaxAmount,
  approvalLimitExceededMessage,
  unpricedRows,
  PO_PROPOSAL_EDITABLE_STATUSES,
} from "./constants"

// Need every proposal line priced first (BE 400s submit/resubmit and blocks
// approve until then).
const PRICE_GATED_ACTIONS = ["submit", "resubmit", "approve"]

const PO_STEPS = [
  { key: PO_STATUS.DRAFT, label: "Draft" },
  { key: PO_STATUS.PENDING, label: "Waiting for approval" },
  { key: PO_STATUS.APPROVED, label: "Approved" },
  { key: PO_STATUS.ISSUED, label: "Issued" },
  { key: PO_STATUS.RECEIVING, label: "Receiving" },
  { key: PO_STATUS.COMPLETED, label: "Completed" },
]

// Internal-transfer PO: what the source warehouse holds against what the PO needs.
const InternalStockCard = ({ stock }) => (
  <Card
    icon={<PackageCheck aria-hidden />}
    title="Internal stock"
    subtitle={`${stock.warehouseName || `#${stock.warehouseCenterId}`} · ${
      stock.stockIssued ? "Issued" : stock.enough ? "In stock" : "Short"
    }`}
  >
    {stock.offCatalogLineIds?.length > 0 && (
      <Alert tone="danger">
        {stock.offCatalogLineIds.length} off-catalog line(s) — move them to another PO.
      </Alert>
    )}
    <table className="pc-table">
      <thead>
        <tr>
          <th>Item</th>
          <th className="pc-th-right">Needed</th>
          <th className="pc-th-right">On hand</th>
          <th className="pc-th-right">Short</th>
        </tr>
      </thead>
      <tbody>
        {(stock.items || []).map((it) => (
          <tr key={it.itemId}>
            <td>{it.itemName}</td>
            <td className="pc-td-right">{it.neededQty}</td>
            <td className="pc-td-right">{it.onHandQty}</td>
            <td
              className="pc-td-right"
              style={it.shortQty > 0 ? { color: "var(--pc-danger, #b42318)", fontWeight: 600 } : undefined}
            >
              {it.shortQty > 0 ? it.shortQty : "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </Card>
)

const paymentMethodLabel = (code) =>
  PAYMENT_METHOD_OPTIONS.find((o) => o.value === code)?.label || code || "—"

// One label/value pair in a Purchase Information / Financial grid.
const Field = ({ label, value, full, strong }) => (
  <div className={`pc-def${full ? " pc-def-full" : ""}`}>
    <div className="pc-def-label">{label}</div>
    <div className={`pc-def-value${strong ? " pc-def-value--strong" : ""}`}>{value ?? "—"}</div>
  </div>
)

// Fail-closed approval: when Admin hasn't configured an approval_rule for
// purchases, submit leaves the PO pending and reports an approval error rather
// than auto-approving. Detect it from the returned detail (best-effort field
// names) so we can warn instead of crashing.
const approvalNotConfigured = (po) =>
  po?.approvalConfigured === false ||
  po?.approvalRuleConfigured === false ||
  /approval.*(not|chưa).*config|no approval rule/i.test(po?.decisionMessage || "")

const PurchaseDetailContainer = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: po, isLoading, isError, refetch, isFetching } = useGetPurchaseQuery(id, { skip: !id })
  const location = useLocation()
  const pricesEditable = PO_PROPOSAL_EDITABLE_STATUSES.includes(po?.status)
  const { data: readiness, refetch: refetchReadiness } = useGetPurchasePriceReadinessQuery(id, {
    skip: !id || !pricesEditable,
  })
  const pricesMissing = pricesEditable && readiness?.ready === false
  const [priceDialogOpen, setPriceDialogOpen] = useState(false)
  const [receiptsOpen, setReceiptsOpen] = useState(false)

  // Create PO lands here with state.checkPrices: open the popup once if the
  // new PO still has unpriced lines.
  const checkedAfterCreate = useRef(false)
  useEffect(() => {
    if (!location.state?.checkPrices || checkedAfterCreate.current || !readiness) return
    checkedAfterCreate.current = true
    if (!readiness.ready) setPriceDialogOpen(true)
  }, [readiness, location.state])

  // Selecting another vendor's proposal re-prices the PO server-side.
  const checkPricesAfterSelect = async () => {
    try {
      const next = await refetchReadiness().unwrap()
      if (!next?.ready) setPriceDialogOpen(true)
    } catch {
      // The banner still reflects readiness once the PO refetches.
    }
  }

  const guardAction = async (key) => {
    if (key === "issue" && po?.internalTransfer && po.internalStock && !po.internalStock.enough) {
      showToastWarning(`${po.internalStock.warehouseName || "Warehouse"} doesn't have enough stock — can't issue yet.`)
      return false
    }
    if (PRICE_GATED_ACTIONS.includes(key) && pricesMissing) {
      showToastWarning("PO còn dòng chưa có giá — nhập đủ giá trước.")
      setPriceDialogOpen(true)
      return false
    }
    return true
  }

  const [submitPurchase] = useSubmitPurchaseMutation()
  const [purchaseAction] = usePurchaseActionMutation()
  const [resubmitPurchase] = useResubmitPurchaseMutation()
  const [issuePurchase] = useIssuePurchaseMutation()
  const [receivePurchase] = useReceivePurchaseMutation()
  const [cancelPurchase] = useCancelPurchaseMutation()
  const [sendToVendor] = useSendPurchaseToVendorMutation()
  const [resendToVendor] = useResendPurchaseToVendorMutation()
  const [busy, setBusy] = useState(false)

  const back = () => navigate("/procurement/purchases")

  // Dispatch one lifecycle action, then refetch so status + allowed_actions
  // re-render from the server truth. Returns true on success (closes the modal).
  // `deadline` only applies to info_request (see PurchaseActionBar).
  const runAction = async (action, noteOrReceivePayload, deadline) => {
    if (busy) return false
    setBusy(true)
    try {
      let result
      if (action === "send_to_vendor" || action === "resend_to_vendor") {
        const sent = await (action === "send_to_vendor" ? sendToVendor(id) : resendToVendor(id)).unwrap()
        showToastSuccess(sent?.sentTo ? `${sent.message || "Sent"} (${sent.sentTo})` : sent?.message || "Sent to the supplier.")
        refetch()
        return true
      }
      if (action === "submit") result = await submitPurchase(id).unwrap()
      else if (action === "issue") result = await issuePurchase(id).unwrap()
      else if (action === "receive") result = await receivePurchase({ id, ...noteOrReceivePayload }).unwrap()
      else if (action === "resubmit") result = await resubmitPurchase({ id, note: noteOrReceivePayload }).unwrap()
      else if (action === "cancel") result = await cancelPurchase({ id, reason: noteOrReceivePayload }).unwrap()
      else result = await purchaseAction({ id, action, note: noteOrReceivePayload, deadline }).unwrap()

      if (action === "submit" && approvalNotConfigured(result)) {
        showToastWarning(
          "No approval workflow is configured for Purchase yet. The PO is waiting — ask an Admin to set up the approval rule."
        )
      } else if (isAutoRejectedOutcome(action, result, PO_STATUS.REJECTED)) {
        // A 200 here only means the call went through — if this PO's
        // info-request deadline had already passed (most likely hit via
        // resubmit), the backend auto-rejects instead of applying the action,
        // and a green success toast would misreport that.
        showToastError(AUTO_REJECT_MESSAGE)
      } else {
        showToastSuccess("Done. The PO has been updated.")
      }
      refetch()
      return true
    } catch (e) {
      const status = e?.status
      if (action === "send_to_vendor" || action === "resend_to_vendor") {
        // 400 no supplier email, 409 already sent, 429 resend too soon — the
        // backend's own message says what to do.
        showToastError(getApiErrorMessage(e, "Couldn't send this PO to the supplier. Please try again."))
        if (status === 409) refetch()
        return false
      }
      if (status === 400 && (action === "submit" || action === "resubmit")) {
        // No selected proposal / unpriced lines — the price popup lets staff fill them in.
        showToastError(getApiErrorMessage(e, "Couldn't submit this PO."))
        await checkPricesAfterSelect()
        return true
      }
      if (status === 409) {
        // cancel's 409 also lists unfinished payment requests — show the BE's
        // message instead of the generic "already updated".
        const cancelMsg = action === "cancel" ? e?.data?.data?.message || e?.data?.message : null
        showToastError(cancelMsg || "This PO was already updated. Reloading…")
        refetch()
      } else if (status === 403) {
        const limitMsg = approvalLimitExceededMessage(
          e?.data?.data?.message || e?.data?.message
        )
        showToastError(limitMsg || "You're not allowed to perform that action.")
      } else if (status === 422 || approvalNotConfigured(e?.data)) {
        showToastWarning(
          "No approval workflow is configured for Purchase yet. Ask an Admin to set up the approval rule."
        )
        refetch()
      } else {
        const msg = e?.data?.data?.message || e?.data?.message || "Couldn't complete that action. Please try again."
        showToastError(msg)
      }
      return false
    } finally {
      setBusy(false)
    }
  }

  const crumbs = ["Procurement", { label: "Purchases", to: "/procurement/purchases" }, po?.poCode || id]

  if (isLoading) {
    return (
      <Page className="pc-page--refined pc-page--detail" breadcrumb={crumbs} title={po?.poCode || "Purchase Order"}>
        <Loading />
      </Page>
    )
  }

  if (isError || !po) {
    return (
      <Page className="pc-page--refined pc-page--detail" breadcrumb={crumbs} title="Purchase Order">
        <Card>
          <div className="pc-section-empty">
            <div className="pc-section-empty-title">Couldn&apos;t load this PO</div>
            The purchase order couldn&apos;t be loaded. It may have been removed, or the service is
            unavailable.
            <div style={{ marginTop: 12 }}>
              <Button variant="secondary" size="sm" onClick={refetch}>
                Retry
              </Button>
              <Button variant="ghost" size="sm" onClick={back}>
                Back to Purchases
              </Button>
            </div>
          </div>
        </Card>
      </Page>
    )
  }

  const linkedOrders = po.linkedOrders || []
  const paymentDetail = po.paymentDetail || []
  const actionLog = po.actionLog || []
  const approvalProgress = po.approvalProgress || []
  const documents = po.documents || []
  const vendorProposals = po.vendorProposals || []
  const proposalsLocked = !pricesEditable
  const selectedProposal = vendorProposals.find((p) => p.isSelected)
  const canFillPrices = canEditPurchase(po) || (po.allowedActions || []).includes("approve")
  const pricesBlocker = !pricesMissing
    ? null
    : readiness.issue === "NO_VENDOR"
    ? "PO chưa có nhà cung cấp — chọn NCC để hệ thống điền giá. Chưa thể submit/approve."
    : `Còn ${unpricedRows(readiness).length} dòng chưa có giá trên báo giá đang chọn${
        readiness.vendorName ? ` (${readiness.vendorName})` : ""
      } — cần đủ giá trước khi submit/approve.`
  const isInternal = !!po.internalTransfer
  const internalStock = po.internalStock
  // Internal POs have no supplier email, so nothing to send.
  const sidebarActions = isInternal
    ? (po.allowedActions || []).filter((a) => a !== "send_to_vendor" && a !== "resend_to_vendor")
    : po.allowedActions
  const receiptProgress = po.receiptProgress
  const receiptLines = receiptProgress?.lines || []
  const receiptPct =
    receiptProgress?.totalLines > 0
      ? Math.min(100, (Number(receiptProgress.receivedLines) / Number(receiptProgress.totalLines)) * 100)
      : 0

  return (
    <Page
      className="pc-page--refined pc-page--detail"
      breadcrumb={crumbs}
      title={
        <span className="pc-title-row">
          {po.poCode || "Purchase Order"}
          <StatusBadge status={po.status} />
          {po.deliveryStatus && <StatusBadge status={po.deliveryStatus} />}
          {isInternal && (
            <StatusBadge
              status="info"
              label={`Internal transfer · ${internalStock?.warehouseName || `#${po.sourceCenterId}`}`}
            />
          )}
        </span>
      }
      subtitle={
        po.createdAt
          ? `Created on ${formatDate(po.createdAt)}${po.createdBy ? ` by User #${po.createdBy}` : ""}`
          : undefined
      }
      actions={
        canEditPurchase(po) && (
          <Button
            variant="primary"
            leftIcon={<Pencil aria-hidden />}
            onClick={() => navigate(`/procurement/purchases/${id}/edit`)}
          >
            {po.status === PO_STATUS.INFO_REQUESTED ? "Edit & Resubmit" : "Edit"}
          </Button>
        )
      }
    >
      {/* approvalError: BE couldn't resolve an approver for a step (e.g. rule
          references a role no one holds) — the PO is stuck until an Admin
          fixes the approval rule. */}
      {po.approvalError && (
        <Alert tone="danger">
          This PO&apos;s approval routing has an error — an Admin needs to check the approval rule
          configuration before it can move forward.
        </Alert>
      )}

      {pricesBlocker && (
        <Alert
          tone="warn"
          action={
            canFillPrices && (
              <Button size="sm" variant="secondary" onClick={() => setPriceDialogOpen(true)}>
                {readiness.issue === "NO_VENDOR" ? "Chọn NCC" : "Nhập giá"}
              </Button>
            )
          }
        >
          {pricesBlocker}
        </Alert>
      )}

      {isInternal && internalStock && !internalStock.enough && !internalStock.stockIssued && (
        <Alert tone="danger">
          {internalStock.warehouseName || "Warehouse"} doesn&apos;t have enough stock — Issue is blocked.
        </Alert>
      )}

      <Card className="pc-progress-card">
        <ProcessStepper steps={PO_STEPS} currentKey={po.status} />
      </Card>

      <div className="pc-review">
        <div className="pc-review-main">
          <Card
            icon={<ListChecks aria-hidden />}
            title="Purchase Information"
            subtitle="General information about this purchase"
          >
            <div className="pc-defgrid">
              <Field label="Purchase code" value={po.poCode} />
              <Field label="Created date" value={formatDate(po.createdAt)} />
              <Field label="Created by" value={po.createdBy ? `User #${po.createdBy}` : undefined} />
              <Field label="Supplier" value={selectedProposal?.supplierName || po.supplierName} />
              <Field label="Sent to supplier" value={po.sentToVendorAt ? formatDate(po.sentToVendorAt) : "Not sent yet"} />
              <Field label="Category" value={po.categoryName} />
              <Field label="Region" value={po.region} />
            </div>
          </Card>

          {isInternal && internalStock && <InternalStockCard stock={internalStock} />}

          {!isInternal && (
            <Card
              icon={<CircleDollarSign aria-hidden />}
              title="Financial Information"
              subtitle="Payment details and total amount"
            >
            <div className="pc-defgrid">
              <Field label="Payment method" value={paymentMethodLabel(po.paymentMethod)} />
              <Field label="Paid status" value={po.paidStatus ? <StatusBadge status={po.paidStatus} /> : "—"} />
              <Field label="Total amount" value={formatVND(po.total)} strong full />
              {po.deliveryFee != null && (
                <Field label="Delivery fee (not included in total)" value={formatVND(po.deliveryFee)} />
              )}
              {po.status === PO_STATUS.CANCELLED && <Field label="Cancel reason" value={po.reason} full />}
            </div>
            </Card>
          )}

          {/* Linked orders — the approved order requests this PO bundles. */}
          <Card
            icon={<Link2 aria-hidden />}
            title="Linked Orders"
            subtitle={`${linkedOrders.length} order(s)`}
          >
            {linkedOrders.length === 0 ? (
              <p className="pc-section-empty">No linked orders.</p>
            ) : (
              <div className="pc-mini-table-wrap">
                <table className="pc-mini-table">
                  <thead>
                    <tr>
                      <th>Order code</th>
                      <th>Title</th>
                      <th className="pc-mini-right">Total amount</th>
                      <th className="pc-mini-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linkedOrders.map((o) => (
                      <tr key={o.id ?? o.orCode ?? o.code}>
                        <td className="pc-mini-strong">{o.orCode || o.code || o.id}</td>
                        <td>{o.title || "—"}</td>
                        <td className="pc-mini-right pc-mini-strong">{formatVND(o.total ?? o.totalAmount)}</td>
                        <td className="pc-mini-right">
                          {(o.id || o.orderId) && (
                            <Button
                              size="sm"
                              variant="secondary"
                              leftIcon={<Eye aria-hidden />}
                              onClick={() => navigate(`/procurement/orders/${o.id ?? o.orderId}`)}
                            >
                              View
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="pc-mini-strong" colSpan={2}>Total</td>
                      <td className="pc-mini-right pc-mini-strong">
                        {formatVND(
                          linkedOrders.reduce((sum, o) => sum + (Number(o.total ?? o.totalAmount) || 0), 0)
                        )}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Card>

          {/* The PO's own proposals (po.vendorProposals) — the selected one
              decides this PO's vendor and total. Mutations refetch this
              detail via the PURCHASE tag; Refresh covers vendor updates
              made through the public link. */}
          <div id="po-vendor-proposals" />
          <VendorProposalsSection
            proposals={vendorProposals}
            sourceType="purchase"
            sourceId={po.id}
            locked={proposalsLocked}
            onRefresh={() => {
              refetch()
              if (pricesEditable) refetchReadiness()
            }}
            refreshing={isFetching}
            onSelected={checkPricesAfterSelect}
          />

          {/* D — Goods Receipt: per-line quantity + quality, shown once the PO
              is in a goods-receipt phase. Recording a receipt doesn't change
              the PO status. */}
          {[PO_STATUS.ISSUED, PO_STATUS.RECEIVING, PO_STATUS.COMPLETED].includes(po.status) && (
            <GoodsReceiptPanel
              purchaseId={po.id ?? id}
              progressLines={receiptLines}
              readOnly={po.status === PO_STATUS.COMPLETED}
            />
          )}

          {/* Goods receipt — documents uploaded via the "Receive Goods" action
              (POST /purchases/{id}/receive). One row per upload; status/stage
              itself lives on the PO header and renders in the stepper above. */}
          <Card
            icon={<PackageCheck aria-hidden />}
            title="Goods Receipt"
            subtitle={`${po.goodsReceiptsCount ?? documents.length} receipt(s) · ${documents.length} document(s)`}
            action={
              <Button size="sm" variant="secondary" leftIcon={<History aria-hidden />} onClick={() => setReceiptsOpen(true)}>
                Receipt history
              </Button>
            }
          >
            {receiptProgress && (
              <div className="pc-receipt-progress">
                <div className="pc-receipt-progress-head">
                  <span>
                    <strong>
                      {receiptProgress.receivedLines ?? 0}/{receiptProgress.totalLines ?? 0}
                    </strong>{" "}
                    lines fully received
                  </span>
                  <StatusPill
                    tone={receiptStatusOf(receiptProgress.status).tone}
                    label={receiptStatusOf(receiptProgress.status).label}
                  />
                </div>
                <ProgressBar pct={receiptPct} color={receiptPct >= 100 ? "var(--pc-accent)" : undefined} />
                {receiptLines.length > 0 && (
                  <div style={{ display: "grid", gap: 6, marginTop: 10 }}>
                    {receiptLines.map((l) => {
                      const st = receiptStatusOf(l.status)
                      return (
                        <div
                          key={l.orderRequestLineId}
                          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, fontSize: 13 }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <div style={{ overflowWrap: "anywhere" }}>{l.itemName || "—"}</div>
                            {l.orderCode && <div style={{ fontSize: 12, color: "var(--pc-muted)" }}>{l.orderCode}</div>}
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                            <strong>
                              {l.receivedQty ?? 0} / {l.orderedQty ?? 0}
                            </strong>
                            <StatusPill tone={st.tone} label={st.label} />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
            {documents.length === 0 ? (
              <p className="pc-section-empty">No goods receipt recorded yet.</p>
            ) : (
              <div className="pc-attach-group pc-attach-group--flush">
                {documents.map((d) => (
                  <div key={d.id} className="pc-attach-row">
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="pc-attach-name">{d.fileName}</div>
                      <div className="pc-attach-type">{formatDateTime(d.uploadedAt)}</div>
                    </div>
                    {d.fileUrl && (
                      <a
                        className="pc-btn pc-btn--secondary pc-btn--sm"
                        href={d.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Approval progress — resolved chain (approver names, mode, quorum)
              with each step's live state + logged actions. */}
          <Card
            icon={<ListChecks aria-hidden />}
            title="Approval Progress"
            subtitle={po.ruleVersion ? `Rule v${po.ruleVersion}` : "Who needs to approve this purchase"}
          >
            {approvalProgress.length === 0 ? (
              <p className="pc-section-empty">No approval route resolved yet.</p>
            ) : (
              <LifecycleTimeline
                items={approvalProgress.map((step, idx) => ({
                  title: `Step ${step.stepOrder ?? idx + 1} · ${
                    (step.approverNames || []).join(", ") || "—"
                  }`,
                  subtitle: [
                    step.mode === "parallel" ? "All at once" : "One at a time",
                    `needs ${step.quorum || 1} approver${(step.quorum || 1) > 1 ? "s" : ""}`,
                    formatMaxAmount(step.maxAmount),
                  ]
                    .filter(Boolean)
                    .join(" · "),
                  done: step.state === "done",
                  current: step.state === "current",
                  content:
                    step.actions && step.actions.length > 0 ? (
                      <div className="pc-step-actions">
                        {step.actions.map((a, ai) => (
                          <div key={ai}>
                            <strong>{PO_ACTION_META[a.action]?.label || (a.action === "reroute" ? "Rerouted" : a.action)}</strong> by
                            user #{a.actor} · {formatDateTime(a.actedAt)}
                            {a.note ? ` — "${a.note}"` : ""}
                          </div>
                        ))}
                      </div>
                    ) : null,
                }))}
              />
            )}
          </Card>

          {/* Payment — module ships separately (Payment Requests, F1–F5);
              payment_detail[] is empty for now. Render an empty state, NOT
              an error. */}
          {!isInternal && (
          <Card icon={<Wallet aria-hidden />} title="Payment" subtitle="Payment records for this purchase">
            {paymentDetail.length === 0 ? (
              <p className="pc-section-empty">No payment records yet.</p>
            ) : (
              <div className="pc-attach-group pc-attach-group--flush">
                {paymentDetail.map((p, idx) => (
                  <div key={p.id ?? idx} className="pc-attach-row">
                    <div className="pc-attach-name" style={{ flex: 1 }}>
                      {p.description || p.invoiceNumber || `Line ${idx + 1}`}
                    </div>
                    <strong>{formatVND(p.amount)}</strong>
                  </div>
                ))}
              </div>
            )}
          </Card>
          )}
        </div>

        {/* Action sidebar — buttons driven entirely by allowed_actions[]. */}
        <aside className="pc-review-side">
          <PurchaseActionBar
            allowedActions={sidebarActions}
            po={po}
            busy={busy}
            onAct={runAction}
            guard={guardAction}
          />

          {/* Action log — approve/reject/info_request/resubmit/etc. history. */}
          <Card
            icon={<History aria-hidden />}
            title="Activity Log"
            subtitle={`${actionLog.length} entr${actionLog.length === 1 ? "y" : "ies"}`}
          >
            {actionLog.length === 0 ? (
              <p className="pc-section-empty">No actions logged yet.</p>
            ) : (
              <ol className="pc-activity">
                {actionLog.map((a, idx) => (
                  <li key={idx} className={idx === actionLog.length - 1 ? "is-latest" : undefined}>
                    <div className="pc-activity-title">
                      {PO_ACTION_META[a.action]?.label || (a.action === "reroute" ? "Rerouted" : a.action) || "Action"}
                    </div>
                    <div className="pc-activity-meta">
                      {[a.actor ? `User #${a.actor}` : null, formatDateTime(a.actedAt)].filter(Boolean).join(" · ")}
                    </div>
                    {a.note && <div className="pc-activity-note">{a.note}</div>}
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </aside>
      </div>
      {pricesEditable && readiness && (
        <MissingPricesDialog
          open={priceDialogOpen}
          onClose={() => setPriceDialogOpen(false)}
          po={po}
          readiness={readiness}
        />
      )}
      <GoodsReceiptHistoryDrawer
        purchaseId={po.id ?? id}
        poCode={po.poCode}
        open={receiptsOpen}
        onClose={() => setReceiptsOpen(false)}
      />
    </Page>
  )
}

export default PurchaseDetailContainer
